<template>
    <div class="tile-content tile-content-secondary">
        <p>Schedule Payment Retry allows you to set multiple retries for subscriptions that fail to bill successfully, and define whether to cancel the account after the last attempt. You can even schedule discounts or partial payments.</p>
        <div class="divider divider-tile-content "></div>
        <h3 class="stack-m">Retry Attempts</h3>
        <p class="stack-m">If a scheduled billing attempt fails, Rebilly will retry according to the following schedule:</p>
        <div v-if="hasAttempts">
            <table class="table">
                <colgroup>
                    <col style="width:50px;">
                    <col width="50%">
                    <col width="50%">
                    <col style="width:50px;">
                </colgroup>
                <thead>
                <tr>
                    <th>Order</th>
                    <th>Schedule type</th>
                    <th>Payment Adjustment</th>
                    <th></th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(attempt, index) in action.attempts">
                    <td>
                        <h2>{{ index + 1 }}</h2>
                    </td>
                    <td>
                        <define-schedule-view
                            :format-type-drop-down-label="formatScheduleTypeDropDownLabel"
                            :schedule-instruction="attempt.scheduleInstruction"
                            :validate="$v.action.attempts[index].scheduleInstruction"
                            @update="data => updateSchedule(index, data)"/>
                    </td>
                    <td>
                        <define-payment-view
                            :payment-instruction="attempt.paymentInstruction"
                            :validate="$v.action.attempts[index].paymentInstruction"
                            @update="data => updatePayment(index, data)"/>
                    </td>
                    <td>
                        <v-icon-button type="basic" @click="deleteAttempt(index)">
                            <v-icon icon="trash"/>
                        </v-icon-button>
                    </td>
                </tr>
                </tbody>
            </table>
        </div>
        <div>
            <div class="divider no-margin-top"></div>
            <r-button type="link" @click="addAttempt">Add Attempt</r-button>
        </div>
        <div class="divider divider-tile-content"></div>
        <h3 class="stack-m">After Each Attempt</h3>
        <p class="stack-m">You may wish to push the renewal time into the future according to your plan schedule after each retry attempt. Otherwise the next rebill may occur while you are still within the retry schedule of the current rebill.</p>
        <div class="grid-content">
            <div class="grid-item grid-item-half grid-item-half-break">
                <v-select
                    :options="successPolicyOptions"
                    placeholder="Type to search or select"
                    :validate="$v.action.afterAttemptPolicy"
                    v-model="action.afterAttemptPolicy"
                />
            </div>
        </div>
        <div class="divider divider-tile-content"></div>
        <h3 class="stack-m">After the Last Failed Attempt</h3>
        <p class="stack-m">If all of the retry attempts fail you can choose between doing nothing or canceling the subscription.</p>
        <div class="grid-content">
            <div class="grid-item grid-item-half grid-item-half-break">
                <v-select
                    :options="afterPaymentRetryEndPolicyOptions"
                    placeholder="Type to search or select"
                    :validate="$v.action.afterRetryEndPolicy"
                    v-model="action.afterRetryEndPolicy"
                />
            </div>
        </div>
    </div>
</template>

<script>
    import _ from '@/common/helpers';
    import {required} from 'vuelidate/lib/validators';
    import SchedulePaymentRetryActionModel, {
        PaymentRetryModel,
    } from '@/common/models/rules/actions/schedule-payment-retry-action-model';
    import {vSelect, vIcon, vIconButton} from '@/components/form';
    import DefineScheduleView from '@/modules/rules-engine/components/actions/schedule-payment/define-schedule-view';
    import DefinePaymentView from '@/modules/rules-engine/components/actions/schedule-payment/define-payment-view';
    import getScheduleValidation
        from '@/modules/rules-engine/components/actions/schedule-payment/get-schedule-validation';
    import getPaymentValidation
        from '@/modules/rules-engine/components/actions/schedule-payment/get-payment-validation';
    import BaseAction from '@/modules/rules-engine/actions/base-action';

    export default {
        name: 'schedule-payment-retry',
        extends: BaseAction,
        components: {
            vIcon,
            vIconButton,
            vSelect,
            DefinePaymentView,
            DefineScheduleView,
        },
        computed: {
            hasAttempts() {
                return this.action.attempts.length > 0;
            },
        },
        validations() {
            const validations = {
                action: {
                    attempts: {},
                    afterAttemptPolicy: {
                        required,
                    },
                    afterRetryEndPolicy: {
                        required,
                    },
                },
            };

            validations.action.attempts = this.action.attempts.reduce((memo, attempt, index) => ({
                ...memo,
                [index]: {
                    scheduleInstruction: getScheduleValidation(attempt.scheduleInstruction),
                    paymentInstruction: getPaymentValidation(attempt.paymentInstruction),
                },
            }), {});
            return validations;
        },
        data() {
            return {
                afterPaymentRetryEndPolicyOptions: Object.values(
                    SchedulePaymentRetryActionModel.AfterRetryEndPolicies,
                ).map(value => ({value, label: this.changeAfterAttemptLabel(value)})),
                successPolicyOptions: Object.values(
                    SchedulePaymentRetryActionModel.AfterAttemptPolicies,
                ).map(value => ({value, label: this.changeAfterAttemptLabel(value)})),
            };
        },
        methods: {
            changeAfterAttemptLabel(value) {
                return value === SchedulePaymentRetryActionModel.AfterAttemptPolicies.none
                    ? 'Do Nothing'
                    : _.unKebabCase(value);
            },
            formatScheduleTypeDropDownLabel({value, formatter}) {
                return `Retry ${formatter({value})}`;
            },
            addAttempt() {
                /**
                 * todo: ScheduleInstructionModel and PaymentInstructionModel
                 * will crashing if the params are null
                 */
                this.action.attempts.push(new PaymentRetryModel({
                    scheduleInstruction: {},
                    paymentInstruction: {},
                }));
            },
            deleteAttempt(index) {
                this.action.attempts.splice(index, 1);
            },
            updateAttempt(index, {scheduleInstruction = {}, paymentInstruction = {}}) {
                const origin = this.action.attempts[index];
                const data = {
                    scheduleInstruction: {
                        ...origin.scheduleInstruction,
                        ...scheduleInstruction,
                    },
                    paymentInstruction: {
                        ...origin.paymentInstruction,
                        ...paymentInstruction,
                    },
                };
                this.action.attempts.splice(index, 1, new PaymentRetryModel(data));
            },
            updateSchedule(index, scheduleInstruction) {
                this.updateAttempt(index, {scheduleInstruction});
            },
            updatePayment(index, paymentInstruction) {
                this.updateAttempt(index, {paymentInstruction});
            },
            getValidationAggregate() {
                return {
                    name: 'schedule-payment-retry',
                    rules: [
                        {
                            name: `The Schedule Retry Payment fields are invalid.`,
                            delegate: () => {
                                this.$v.$touch();
                                return this.$v.$error;
                            },
                        },
                    ],
                };
            },
        },
    };
</script>
